import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import instance from "../instance";
import "./Subscriptions.css";

const CustomerOrders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const code = params.get("code");
  const customerName = params.get("name");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(40);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [statusStats, setStatusStats] = useState({
    success: 0,
    pending: 0,
    cancelled: 0,
    failed: 0
  });
  const [totalSuccessAmount, setTotalSuccessAmount] = useState(0);
  const [totalManualAmount, setTotalManualAmount] = useState(0);
  const [totalKashierAmount, setTotalKashierAmount] = useState(0);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [transactions, setTransactions] = useState({});
  const [loadingTransactions, setLoadingTransactions] = useState(new Set());
  const [logs, setLogs] = useState({});
  const [loadingLogs, setLoadingLogs] = useState(new Set());
  const [activeTab, setActiveTab] = useState({});
  const [searchFilters, setSearchFilters] = useState({
    merchant_order_id: "",
    status: "",
    transaction_id: "",
    paymentExp: ""
  });

  const fetchOrders = async (page = 1, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      const searchParams = new URLSearchParams({
        page: page,
        limit: limit
      });

      // إضافة معاملات البحث المنفصلة لكل حقل (تطابق 100%)
      Object.entries(searchFilters).forEach(([key, value]) => {
        if (value.trim() !== "") {
          // معالجة خاصة لحقل التاريخ
          if (key === "paymentExp") {
            // التأكد من تنسيق التاريخ الصحيح
            const dateValue = value.trim();
            if (dateValue) {
              searchParams.append(key, dateValue);
            }
          } else {
            searchParams.append(key, value.trim());
          }
        }
      });

      console.log('Fetching orders for customer code:', code);
      
      const response = await instance.get(`/order/${code}/orders?${searchParams.toString()}`);

      console.log('API Response:', {
        code: response.data.code,
        page: response.data.page,
        limit: response.data.limit,
        total: response.data.total,
        totalPages: response.data.totalPages,
        dataLength: response.data.data.length
      });

      if (append) {
        setOrders(prevOrders => [...prevOrders, ...response.data.data]);
      } else {
        setOrders(response.data.data);
      }
      
      // استخدام الإحصائيات من API
      if (response.data.stats) {
        setStatusStats(response.data.stats);
      }
      
      // حفظ إجمالي المبالغ
      if (response.data.totalSuccessAmount !== undefined) {
        setTotalSuccessAmount(response.data.totalSuccessAmount);
      }
      if (response.data.totalManualAmount !== undefined) {
        setTotalManualAmount(response.data.totalManualAmount);
      }
      if (response.data.totalKashierAmount !== undefined) {
        setTotalKashierAmount(response.data.totalKashierAmount);
      }
      
      setTotalPages(response.data.totalPages);
      setTotal(response.data.total);
      setCurrentPage(response.data.page);
      setHasMore(response.data.page < response.data.totalPages);
      setError(null);
    } catch (err) {
      setError("An error occurred while fetching customer orders");
      console.error("Error fetching customer orders:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (code) {
      setOrders([]);
      setCurrentPage(1);
      fetchOrders(1);
    } else {
      setError("No customer code provided");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchOrders is recreated each render; adding it would refetch on every paint
  }, [code]);

  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // تحميل المزيد عند الوصول لأسفل الصفحة تقريباً
      if (scrollTop + windowHeight >= documentHeight - 100) {
        const nextPage = currentPage + 1;
        if (nextPage <= totalPages) {
          fetchOrders(nextPage, true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchOrders is recreated each render; adding it would rebind the scroll listener every paint
  }, [currentPage, totalPages, loadingMore, hasMore]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedOrders = [...orders].sort((a, b) => {
      if (a[key] < b[key]) {
        return direction === 'asc' ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setOrders(sortedOrders);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    
    // Convert to 12-hour format with AM/PM
    const amPm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12; // Convert 0 to 12 for 12-hour format
    
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds} ${amPm}`;
  };

  const formatCurrency = (amount, currency = "EGP") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (amount) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "success":
        return {
          backgroundColor: "rgba(76, 175, 79, 0.2)",
          color: "black",
          padding: "2px 4px",
          borderRadius: "10%",
        };
      case "pending":
        return {
          backgroundColor: "rgba(255, 193, 7, 0.2)",
          color: "black",
          padding: "2px 4px",
          borderRadius: "10%",
        };
      case "failed":
      case "cancelled":
        return {
          backgroundColor: "rgba(244, 67, 54, 0.2)",
          color: "black",
          padding: "2px 4px",
          borderRadius: "10%",
        };
      default:
        return {};
    }
  };

  const getSpeechStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return {
          backgroundColor: "rgba(76, 175, 79, 0.2)",
          color: "black",
          padding: "2px 4px",
          borderRadius: "10%",
        };
      case "inactive":
        return {
          backgroundColor: "rgba(158, 158, 158, 0.2)",
          color: "black",
          padding: "2px 4px",
          borderRadius: "10%",
        };
      default:
        return {};
    }
  };

  // دالة لجلب الترانساكشنز
  const fetchTransactions = async (orderId) => {
    try {
      setLoadingTransactions(prev => new Set(prev).add(orderId));
      
      const response = await instance.get(`/order/${orderId}/transactions`);
      
      setTransactions(prev => ({
        ...prev,
        [orderId]: response.data.transactions
      }));
    } catch (err) {
      console.error("Error fetching transactions:", err);
      setTransactions(prev => ({
        ...prev,
        [orderId]: []
      }));
    } finally {
      setLoadingTransactions(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  // دالة لجلب اللوجز
  const fetchLogs = async (orderId) => {
    try {
      setLoadingLogs(prev => new Set(prev).add(orderId));
      
      const response = await instance.get(`/order/${orderId}/logs`);
      
      setLogs(prev => ({
        ...prev,
        [orderId]: response.data.logs
      }));
    } catch (err) {
      console.error("Error fetching logs:", err);
      setLogs(prev => ({
        ...prev,
        [orderId]: []
      }));
    } finally {
      setLoadingLogs(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  // دالة للتعامل مع الضغط على السجل
  const handleRowClick = (orderId) => {
    const newExpandedRows = new Set(expandedRows);
    
    if (expandedRows.has(orderId)) {
      // إغلاق السجل
      newExpandedRows.delete(orderId);
    } else {
      // فتح السجل وجلب الترانساكشنز واللوجز
      newExpandedRows.add(orderId);
      setActiveTab(prev => ({ ...prev, [orderId]: 'transactions' }));
      
      if (!transactions[orderId]) {
        fetchTransactions(orderId);
      }
      if (!logs[orderId]) {
        fetchLogs(orderId);
      }
    }
    
    setExpandedRows(newExpandedRows);
  };

  // دالة للتنقل بين التبويبات
  const handleTabChange = (orderId, tabName) => {
    setActiveTab(prev => ({ ...prev, [orderId]: tabName }));
    
    // جلب البيانات إذا لم تكن موجودة
    if (tabName === 'transactions' && !transactions[orderId]) {
      fetchTransactions(orderId);
    } else if (tabName === 'logs' && !logs[orderId]) {
      fetchLogs(orderId);
    }
  };

  const handleSearchFilterChange = (field, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearAllFilters = async () => {
    // مسح الفلاتر أولاً
    setSearchFilters({
      merchant_order_id: "",
      status: "",
      transaction_id: "",
      paymentExp: ""
    });
    
    // إعادة تحميل الجدول للوضع الافتراضي بدون فلاتر
    setOrders([]);
    setCurrentPage(1);
    
    // إعادة التحميل مع الفلاتر الفارغة
    try {
      setLoading(true);
      const searchParams = new URLSearchParams({
        page: 1,
        limit: limit
      });
      
      const response = await instance.get(`/order/${code}/orders?${searchParams.toString()}`);
      setOrders(response.data.data);
      
      // استخدام الإحصائيات من API
      if (response.data.stats) {
        setStatusStats(response.data.stats);
      }
      
      // حفظ إجمالي المبالغ
      if (response.data.totalSuccessAmount !== undefined) {
        setTotalSuccessAmount(response.data.totalSuccessAmount);
      }
      if (response.data.totalManualAmount !== undefined) {
        setTotalManualAmount(response.data.totalManualAmount);
      }
      if (response.data.totalKashierAmount !== undefined) {
        setTotalKashierAmount(response.data.totalKashierAmount);
      }
      
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
      setCurrentPage(1);
      setHasMore(1 < response.data.totalPages);
      setError(null);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setError("Failed to load orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setOrders([]);
    setCurrentPage(1);
    fetchOrders(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const hasActiveFilters = Object.values(searchFilters).some(value => value.trim() !== "");

  if (loading) {
    return (
      <div className="loading-container">
        <div>Loading customer orders...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div>{error}</div>
        <button 
          className="retry-button" 
          onClick={() => fetchOrders(currentPage)}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="subscriptions-container">
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <button onClick={() => navigate("/")} className="button">
          ← Main
        </button>
        <button 
          onClick={() => navigate(`/customer?code=${code}&name=${customerName}`)} 
          className="button"
          style={{ marginLeft: "10px" }}
        >
          ← Back to Customer
        </button>
      </div>
      
      <h2 className="subscriptions-header">
        Orders for Customer: {customerName || `Code ${code}`}
      </h2>

      {/* Advanced Search Filters */}
      <div style={{ 
        marginBottom: "20px",
        padding: "20px",
        backgroundColor: "white",
        borderRadius: "8px",
        border: "1px solid #ddd",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
      }}>
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          marginBottom: "20px",
          paddingBottom: "10px",
          borderBottom: "2px solid #4caf50"
        }}>
          <h3 style={{ margin: 0, color: "#333", fontSize: "18px" }}>🔍 Search Filters</h3>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={handleSearch}
              style={{
                padding: "8px 16px",
                backgroundColor: "#4caf50",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "5px"
              }}
            >
              🔍 Search
            </button>
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#f44336",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold"
                }}
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        <div 
          className="search-filters-container"
          style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
            gap: "15px",
            maxWidth: "100%",
            overflow: "hidden"
          }}>
          {/* Merchant Order ID */}
          <div style={{ 
            backgroundColor: "#f9f9f9", 
            padding: "12px", 
            borderRadius: "6px",
            border: "1px solid #e0e0e0",
            minWidth: "0",
            boxSizing: "border-box"
          }}>
            <label style={{ 
              display: "block", 
              marginBottom: "8px", 
              fontWeight: "bold", 
              fontSize: "14px",
              color: "#333"
            }}>
              📋 Merchant Order ID
            </label>
            <input
              type="text"
              value={searchFilters.merchant_order_id}
              onChange={(e) => handleSearchFilterChange("merchant_order_id", e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter Merchant Order ID..."
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "4px",
                border: "2px solid #ddd",
                fontSize: "14px",
                transition: "border-color 0.3s",
                boxSizing: "border-box",
                maxWidth: "100%"
              }}
              onFocus={(e) => e.target.style.borderColor = "#4caf50"}
              onBlur={(e) => e.target.style.borderColor = "#ddd"}
            />
          </div>

          {/* Status */}
          <div style={{ 
            backgroundColor: "#f9f9f9", 
            padding: "12px", 
            borderRadius: "6px",
            border: "1px solid #e0e0e0",
            minWidth: "0",
            boxSizing: "border-box"
          }}>
            <label style={{ 
              display: "block", 
              marginBottom: "8px", 
              fontWeight: "bold", 
              fontSize: "14px",
              color: "#333"
            }}>
              📊 Order Status
            </label>
            <select
              value={searchFilters.status}
              onChange={(e) => handleSearchFilterChange("status", e.target.value)}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "4px",
                border: "2px solid #ddd",
                fontSize: "14px",
                backgroundColor: "white",
                transition: "border-color 0.3s",
                boxSizing: "border-box",
                maxWidth: "100%"
              }}
              onFocus={(e) => e.target.style.borderColor = "#4caf50"}
              onBlur={(e) => e.target.style.borderColor = "#ddd"}
            >
              <option value="">All Status</option>
              <option value="success">✅ Success</option>
              <option value="pending">⏳ Pending</option>
              <option value="cancelled">❌ Cancelled</option>
              <option value="failed">💥 Failed</option>
            </select>
          </div>

          {/* Transaction ID */}
          <div style={{ 
            backgroundColor: "#f9f9f9", 
            padding: "12px", 
            borderRadius: "6px",
            border: "1px solid #e0e0e0",
            minWidth: "0",
            boxSizing: "border-box"
          }}>
            <label style={{ 
              display: "block", 
              marginBottom: "8px", 
              fontWeight: "bold", 
              fontSize: "14px",
              color: "#333"
            }}>
              💳 Transaction ID
            </label>
            <input
              type="text"
              value={searchFilters.transaction_id}
              onChange={(e) => handleSearchFilterChange("transaction_id", e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter transaction ID..."
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "4px",
                border: "2px solid #ddd",
                fontSize: "14px",
                transition: "border-color 0.3s",
                boxSizing: "border-box",
                maxWidth: "100%"
              }}
              onFocus={(e) => e.target.style.borderColor = "#4caf50"}
              onBlur={(e) => e.target.style.borderColor = "#ddd"}
            />
          </div>

          {/* Payment Expiry */}
          <div style={{ 
            backgroundColor: "#f9f9f9", 
            padding: "12px", 
            borderRadius: "6px",
            border: "1px solid #e0e0e0",
            minWidth: "0",
            boxSizing: "border-box"
          }}>
            <label style={{ 
              display: "block", 
              marginBottom: "8px", 
              fontWeight: "bold", 
              fontSize: "14px",
              color: "#333"
            }}>
              📅 Payment Expiry Date
            </label>
            <input
              type="date"
              value={searchFilters.paymentExp}
              onChange={(e) => handleSearchFilterChange("paymentExp", e.target.value)}
              onKeyPress={handleKeyPress}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "4px",
                border: "2px solid #ddd",
                fontSize: "14px",
                backgroundColor: "white",
                transition: "border-color 0.3s",
                boxSizing: "border-box",
                maxWidth: "100%",
                minHeight: "44px",
                WebkitAppearance: "none",
                MozAppearance: "none",
                appearance: "none"
              }}
              onFocus={(e) => e.target.style.borderColor = "#4caf50"}
              onBlur={(e) => e.target.style.borderColor = "#ddd"}
            />
          </div>
        </div>
      </div>
      
      <div className="subscriptions-stats">
        <span style={{ margin: "0 10px" }}>
          Total Orders: <strong>{total}</strong>
        </span>
        <span style={{ margin: "0 10px" }}>
          Loaded: <strong>{orders.length}</strong> orders
        </span>
        {hasMore && (
          <span style={{ margin: "0 10px", color: "#4caf50" }}>
            Scroll down to load more...
          </span>
        )}
      </div>

      {/* Status Statistics */}
      <div style={{
        marginBottom: "20px",
        padding: "15px",
        backgroundColor: "white",
        borderRadius: "8px",
        border: "1px solid #ddd",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
      }}>
        <h4 style={{ margin: "0 0 15px 0", color: "#333", fontSize: "16px" }}>
          📊 Status Statistics
        </h4>
        
        {/* إجمالي المبلغ للطلبات الناجحة */}
        {totalSuccessAmount > 0 && (
          <div style={{
            marginBottom: "15px",
            padding: "10px",
            backgroundColor: "#e8f5e8",
            borderRadius: "6px",
            border: "1px solid #4caf50",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "18px", fontWeight: "bold", color: "#2e7d32" }}>
              💰 Total Success Amount: {formatCurrency(totalSuccessAmount, "EGP")}
            </div>
          </div>
        )}
        {totalManualAmount > 0 && (
          <div style={{
            marginBottom: "15px",
            padding: "10px",
            backgroundColor: "#e3f2fd",
            borderRadius: "6px",
            border: "1px solid #2196f3",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "16px", fontWeight: "bold", color: "#0d47a1" }}>
              🧾 Total Manual Success Amount: {formatCurrency(totalManualAmount, "EGP")}
            </div>
          </div>
        )}
        {totalKashierAmount > 0 && (
          <div style={{
            marginBottom: "15px",
            padding: "10px",
            backgroundColor: "#fff8e1",
            borderRadius: "6px",
            border: "1px solid #ffb300",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "16px", fontWeight: "bold", color: "#e65100" }}>
              🏦 Total Kashier Success Amount: {formatCurrency(totalKashierAmount, "EGP")}
            </div>
          </div>
        )}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "15px"
        }}>
          <div style={{
            padding: "10px",
            backgroundColor: "#e8f5e8",
            borderRadius: "6px",
            border: "1px solid #4caf50",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#2e7d32" }}>
              ✅ {statusStats.success}
            </div>
            <div style={{ fontSize: "12px", color: "#2e7d32", fontWeight: "bold" }}>
              Success
            </div>
          </div>
          
          <div style={{
            padding: "10px",
            backgroundColor: "#fff3e0",
            borderRadius: "6px",
            border: "1px solid #ff9800",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#f57c00" }}>
              ⏳ {statusStats.pending}
            </div>
            <div style={{ fontSize: "12px", color: "#f57c00", fontWeight: "bold" }}>
              Pending
            </div>
          </div>
          
          <div style={{
            padding: "10px",
            backgroundColor: "#ffebee",
            borderRadius: "6px",
            border: "1px solid #f44336",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#d32f2f" }}>
              ❌ {statusStats.cancelled}
            </div>
            <div style={{ fontSize: "12px", color: "#d32f2f", fontWeight: "bold" }}>
              Cancelled
            </div>
          </div>
          
          <div style={{
            padding: "10px",
            backgroundColor: "#fce4ec",
            borderRadius: "6px",
            border: "1px solid #e91e63",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#c2185b" }}>
              💥 {statusStats.failed}
            </div>
            <div style={{ fontSize: "12px", color: "#c2185b", fontWeight: "bold" }}>
              Failed
            </div>
          </div>
        </div>
      </div>

      <div className="subscriptions-table-container">
        <table className="subscriptions-table">
          <thead>
            <tr>
              <th 
                className="sortable" 
                onClick={() => handleSort("id")}
              >
                ID {sortConfig.key === "id" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort("code")}
              >
                Code {sortConfig.key === "code" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort("merchant_order_id")}
              >
                Merchant Order ID {sortConfig.key === "merchant_order_id" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort("Name")}
              >
                Name {sortConfig.key === "Name" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort("phone")}
              >
                Phone {sortConfig.key === "phone" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort("amount")}
              >
                Amount {sortConfig.key === "amount" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort("price")}
              >
                Price {sortConfig.key === "price" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort("priceType")}
              >
                Price Type {sortConfig.key === "priceType" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort("speech_status")}
              >
                Speech {sortConfig.key === "speech_status" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort("speech_amount")}
              >
                Speech Amount {sortConfig.key === "speech_amount" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort("number_months")}
              >
                Months {sortConfig.key === "number_months" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort("monthlyFees")}
              >
                Monthly Fees {sortConfig.key === "monthlyFees" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort("paymentDate")}
              >
                Payment Date {sortConfig.key === "paymentDate" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort("paymentExp")}
              >
                Payment Expiry {sortConfig.key === "paymentExp" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort("status")}
              >
                Status {sortConfig.key === "status" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort("transaction_id")}
              >
                Transaction ID {sortConfig.key === "transaction_id" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th 
                className="sortable" 
                onClick={() => handleSort("created_at")}
              >
                Created At {sortConfig.key === "created_at" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <React.Fragment key={order.id}>
                <tr 
                  className="order-row"
                  onClick={() => handleRowClick(order.id)}
                  style={{ 
                    cursor: 'pointer',
                    backgroundColor: expandedRows.has(order.id) ? '#f0f8ff' : 'transparent',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!expandedRows.has(order.id)) {
                      e.target.style.backgroundColor = '#f5f5f5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!expandedRows.has(order.id)) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <td data-label="ID">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className={`expand-arrow ${expandedRows.has(order.id) ? 'expanded' : ''}`} style={{ fontSize: '12px' }}>
                        ▶
                      </span>
                      {order.id}
                    </div>
                  </td>
                  <td data-label="Code">{order.code}</td>
                  <td data-label="Merchant Order ID">{order.merchant_order_id}</td>
                  <td data-label="Name">{order.Name}</td>
                  <td data-label="Phone">{order.phone}</td>
                  <td data-label="Amount">{formatCurrency(order.amount, order.currency)}</td>
                  <td data-label="Price">{formatNumber(order.price)}</td>
                  <td data-label="Price Type">{order.priceType}</td>
                  <td data-label="Speech">
                    <span style={getSpeechStatusStyle(order.speech_status)}>
                      {order.speech_status || 'inactive'}
                    </span>
                  </td>
                  <td data-label="Speech Amount">{formatNumber(order.speech_amount)}</td>
                  <td data-label="Number Months">{order.number_months}</td>
                  <td data-label="Monthly Fees">{order.monthlyFees || 0}</td>
                  <td data-label="Payment Date">{formatDate(order.paymentDate)}</td>
                  <td data-label="Payment Expiry">{formatDate(order.paymentExp)}</td>
                  <td data-label="Status">
                    <span style={getStatusStyle(order.status)}>
                      {order.status}
                    </span>
                  </td>
                  <td data-label="Transaction ID">{order.transaction_id || '-'}</td>
                  <td data-label="Created At">{formatDateTime(order.created_at)}</td>
                </tr>
                
                {/* صف الترانساكشنز */}
                {expandedRows.has(order.id) && (
                  <tr className="expandable-row">
                    <td colSpan="17" style={{ padding: 0, backgroundColor: '#f8f9fa' }}>
                      <div style={{ 
                        padding: '20px',
                        borderTop: '2px solid #4caf50',
                        backgroundColor: '#f8f9fa'
                      }}>
                        <h4 style={{ 
                          margin: '0 0 15px 0', 
                          color: '#333',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          📋 Order #{order.id} Details
                        </h4>
                        
                        {/* التبويبات */}
                        <div className="tab-container">
                          <button
                            onClick={() => handleTabChange(order.id, 'transactions')}
                            className={`tab-button ${activeTab[order.id] === 'transactions' ? 'active' : ''}`}
                          >
                            💳 Transactions
                            {loadingTransactions.has(order.id) && (
                              <span style={{ fontSize: '12px' }}>⏳</span>
                            )}
                          </button>
                          <button
                            onClick={() => handleTabChange(order.id, 'logs')}
                            className={`tab-button ${activeTab[order.id] === 'logs' ? 'active' : ''}`}
                          >
                            📝 Activity Logs
                            {loadingLogs.has(order.id) && (
                              <span style={{ fontSize: '12px' }}>⏳</span>
                            )}
                          </button>
                        </div>
                        
                        {/* محتوى التبويبات */}
                        {activeTab[order.id] === 'transactions' && (
                          <div className="tab-content">
                            {loadingTransactions.has(order.id) ? (
                          <div style={{ 
                            textAlign: 'center', 
                            padding: '20px',
                            color: '#666'
                          }}>
                              Loading transactions...
                            </div>
                          ) : transactions[order.id] && transactions[order.id].length > 0 ? (
                            <div style={{ overflowX: 'auto' }}>
                              <table className="transaction-table" style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                backgroundColor: 'white'
                              }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#4caf50', color: 'white' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>ID</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Amount</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Currency</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Order Reference</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Status</th>
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Created At</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {transactions[order.id].map((transaction) => (
                                    <tr key={transaction.id} style={{ borderBottom: '1px solid #eee' }}>
                                      <td data-label="ID" style={{ padding: '12px', fontSize: '14px' }}>{transaction.id}</td>
                                      <td data-label="Amount" style={{ padding: '12px', fontSize: '14px' }}>
                                        {formatCurrency(transaction.amount, transaction.currency)}
                                      </td>
                                      <td data-label="Currency" style={{ padding: '12px', fontSize: '14px' }}>{transaction.currency}</td>
                                      <td data-label="Order Reference" style={{ padding: '12px', fontSize: '14px' }}>{transaction.order_reference}</td>
                                      <td data-label="Status" style={{ padding: '12px', fontSize: '14px' }}>
                                        <span style={getStatusStyle(transaction.status)}>
                                          {transaction.status}
                                        </span>
                                      </td>
                                      <td data-label="Created At" style={{ padding: '12px', fontSize: '14px' }}>
                                        {formatDateTime(transaction.created_at)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div style={{ 
                              textAlign: 'center', 
                              padding: '20px',
                              color: '#666',
                              backgroundColor: 'white',
                              borderRadius: '8px',
                              border: '1px solid #ddd'
                            }}>
                              No transactions found for this order.
                            </div>
                          )}
                          </div>
                        )}
                        
                        {/* تبويب اللوجز */}
                        {activeTab[order.id] === 'logs' && (
                          <div className="tab-content">
                            {loadingLogs.has(order.id) ? (
                              <div style={{ 
                                textAlign: 'center', 
                                padding: '20px',
                                color: '#666'
                              }}>
                                Loading activity logs...
                              </div>
                            ) : logs[order.id] && logs[order.id].length > 0 ? (
                              <div style={{ overflowX: 'auto' }}>
                                <table className="transaction-table" style={{
                                  width: '100%',
                                  borderCollapse: 'collapse',
                                  backgroundColor: 'white'
                                }}>
                                  <thead>
                                    <tr style={{ backgroundColor: '#4caf50', color: 'white' }}>
                                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>ID</th>
                                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Action</th>
                                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Description</th>
                                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Created At</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {logs[order.id].map((log) => (
                                      <tr key={log.id} style={{ borderBottom: '1px solid #eee' }}>
                                        <td data-label="ID" style={{ padding: '12px', fontSize: '14px' }}>{log.id}</td>
                                        <td data-label="Action" style={{ padding: '12px', fontSize: '14px' }}>
                                          <span className="log-action">
                                            {log.action}
                                          </span>
                                        </td>
                                        <td data-label="Description" style={{ padding: '12px', fontSize: '14px', maxWidth: '300px', wordWrap: 'break-word' }}>
                                          {log.description || '-'}
                                        </td>
                                        <td data-label="Created At" style={{ padding: '12px', fontSize: '14px' }}>
                                          {formatDateTime(log.created_at)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div style={{ 
                                textAlign: 'center', 
                                padding: '20px',
                                color: '#666',
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                border: '1px solid #ddd'
                              }}>
                                No activity logs found for this order.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {loadingMore && (
        <div className="loading-container">
          <div>Loading more orders...</div>
        </div>
      )}
      
      {!hasMore && orders.length > 0 && (
        <div className="subscriptions-stats">
          <span style={{ color: "#4caf50", fontWeight: "bold" }}>
            All orders loaded! ({orders.length} of {total})
          </span>
        </div>
      )}
    </div>
  );
};

export default CustomerOrders;
